import {
  formatDate,
  formatTimeRange,
} from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import { getSessionStatusDisplay } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-display';
import type {
  CoachingConfig,
  CoachingSession,
  CoachProfile,
} from '@efdigital/ll-addon-data';
import {
  coachCurrentRole,
  coachDisplayName,
  coachInitials,
} from '@efdigital/ll-xceed-v2/utils/coaching/coach-profile-display';
import type { CoachingPlanner } from '@efdigital/ll-xceed-v2/utils/coaching/coachee/coaching-plan-manager';
import type { TOC } from '@ember/component/template-only';

import { SessionActions } from './session-actions.gts';

interface SessionCardSignature {
  Args: {
    session: CoachingSession;
    coach: CoachProfile;
    coachingConfig: CoachingConfig;
    planner: CoachingPlanner;
  };
  Element: HTMLDivElement;
}

function isPast(session: CoachingSession): boolean {
  return new Date(session.startTime) < new Date();
}

function toDate(date: string): Date {
  return new Date(date);
}

const SessionCard: TOC<SessionCardSignature> = <template>
  <div class="card border-0 shadow-sm rounded-4" ...attributes>
    <div
      class="card-body d-flex flex-row align-items-start gap-3 p-3 px-md-4 py-md-3"
    >
      <span
        class="ll-avatar-md rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 fw-bold fs-4
          {{if (isPast @session) 'text-bg-secondary' 'text-bg-primary'}}"
        aria-hidden="true"
      >{{coachInitials @coach}}</span>

      <div class="flex-grow-1 min-w-0 d-flex flex-column gap-3">
        <div
          class="d-flex flex-column flex-xl-row justify-content-between align-items-start align-items-xl-center gap-3"
        >
          <div class="min-w-0">
            <h3
              class="h5 fw-bold mb-1 text-capitalize"
            >{{@session.sessionType}}</h3>
            <p class="text-body-secondary mb-2">
              {{coachDisplayName @coach}}
              ·
              {{coachCurrentRole @coach}}
            </p>
            <div
              class="d-flex flex-wrap align-items-center gap-3 small text-body-secondary fw-semibold"
            >
              <span>{{formatDate (toDate @session.startTime)}}</span>
              <span>{{formatTimeRange
                  (toDate @session.startTime)
                  (toDate @session.endTime)
                }}</span>
              {{#let
                (getSessionStatusDisplay @session.status)
                as |statusDisplay|
              }}
                <span
                  class="badge rounded-pill bg-{{statusDisplay.color}}-subtle text-{{statusDisplay.color}}-emphasis fw-semibold px-2 py-1 d-flex align-items-center gap-1"
                  title={{statusDisplay.description}}
                >
                  <iconify-icon
                    inline
                    icon={{statusDisplay.icon}}
                    aria-hidden="true"
                  />
                  {{statusDisplay.label}}
                </span>
              {{/let}}
            </div>
          </div>

          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <SessionActions
              @session={{@session}}
              @coach={{@coach}}
              @coachingConfig={{@coachingConfig}}
              @planner={{@planner}}
              @buttonClass="btn-sm"
            />
          </div>
        </div>

        {{#if @session.notes}}
          <div class="rounded-3 p-3 bg-body-secondary">
            <p class="mb-0 small text-body">
              <span class="fw-bold">Notes</span>
              ·
              {{@session.notes}}
            </p>
          </div>
        {{/if}}
      </div>
    </div>
  </div>
</template>;

export default SessionCard;
